import { FC, useEffect, useMemo, Fragment, useRef, RefObject, memo, useState, useCallback } from 'react';
import { BasicAlert, CollapsiblePanel, ICollapsiblePanelComponentProps, IHasChildren } from '@ngt/forms';
import { Box, Button, Chip, Divider, Grid, List, Paper, styled, Tooltip, Typography, ListItemButton, ListItemText } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRepeat, faTableList } from '@fortawesome/pro-duotone-svg-icons';
import { ISourceDataFormLayoutComponents, ISourceDataFormSettings, ISourceDataVerificationEvent, ISourceDataVerificationField, ISourceDataVerificationForm, ISourceDataVerificationSection, SourceDataType } from '@/utilities/forms/SourceDataFormProcessor';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { ISourceDataFormLayoutComponentProps, ISourceDataVerificationItem } from '@/utilities/forms/SourceDataFormProcessor';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import SourceDataExpandingContext, { ISourceDataExpandingContext } from '@/contexts/forms/SourceDataExpandingContext';


// Item contents are cached properly with React.memo
const InnerItem = memo(({ index, item, context}: {index: number; item: ISourceDataVerificationItem; context: IVirtuosoContext}) => {
    
    if (item.type === SourceDataType.Event) {
        return ( 
            <context.Layout.Event {...item} {...context} />
        )
    }

    if (item.type === SourceDataType.Form) {
        return ( 
            <context.Layout.Form {...item}  {...context} />
        )
    }

    if (item.type === SourceDataType.Section) {
        if (item.formSectionMetadata?.repeating) {
            return ( 
                <context.Layout.Collection {...item}  {...context} />
            )
        }

        return ( 
            <context.Layout.Group {...item}  {...context} />
        )
    }

    if (item.type === SourceDataType.Field) {
        return ( 
            <context.Layout.Field {...item}  {...context} />
        )
    }

    return (
        <>
            Unknown Item
        </>
    )
  })
  
interface IVirtuosoContext { 
    Layout: ISourceDataFormLayoutComponents; 
    settings?: ISourceDataFormSettings; 
    events: ISourceDataVerificationEvent[];
    list: ISourceDataVerificationItem[];
    expanded: Record<string, boolean>;
}

// The callback is executed often - don't inline complex components in here.
const virtuosoItemContent = (index: number, item: ISourceDataVerificationItem, context: IVirtuosoContext) => {
    return <InnerItem index={index} item={item} context={context} />
}

const SourceDataFormLayoutExpanding: FC<ISourceDataFormLayoutComponentProps> = ({
    events,
    settings,
    children,
    list,
    Layout
}) => {

    const [expanded, setExpanded] = useState<Record<string, boolean>>({})

    useEffect(() => {
        if (settings?.autoEvents ||
            settings?.autoForms || 
            settings?.autoCollections ||
            settings?.autoGroups) {
            
            const e: Record<string, boolean> = {};

            for (let item of list) {
                if (item.type === SourceDataType.Event) {
                    e[item.eventIdentifier!] = !!settings?.autoEvents;
                }
    
                if (item.type === SourceDataType.Form) {
                    e[item.formIdentifier!] = !!settings?.autoForms;
                }
    
                if (item.type === SourceDataType.Section) {
                    if (item.formSectionMetadata?.repeating) {
                        e[item.formSectionIdentifier!] = !!settings?.autoCollections;
                    }
                    else {
                        e[item.formSectionIdentifier!] = !!settings?.autoGroups;
                    }
                }
            }

            setExpanded(e);
        }
    }, [settings, setExpanded, list])

    const filteredList = useMemo(() => {
        return list.filter(item => {
            if (item.type === SourceDataType.Event) {
                return true;
            }

            if (item.type === SourceDataType.Form) {
                return expanded[item.eventIdentifier!];
            }

            if (item.type === SourceDataType.Section) {
                return expanded[item.eventIdentifier!] && expanded[item.formIdentifier!];
            }

            if (item.type === SourceDataType.Field) {
                return expanded[item.eventIdentifier!] && expanded[item.formIdentifier!] && (!item.formSectionIdentifier || expanded[item.formSectionIdentifier!]);
            }

            return false;
        }, [])
    }, [list, expanded])

    const virtuoso = useRef<VirtuosoHandle>(null);

    const context: ISourceDataExpandingContext = useMemo(() => {
        return {
            toggleExpanding: (identifier: string) => {
                setExpanded(e => {
                    return {
                        ...e,
                        [identifier]: !e[identifier]
                    };
                })
            },
            expanded
        }
    }, [expanded, setExpanded])

    return (
        <>
            { 
                (!events || events.length === 0) && (
                    <BasicAlert
                        title="No Events Configured"
                        description="No events have been included in this configuration."
                        severity="info"
                        icon={faInfoCircle}
                    />
                )
            }
            { 
                list && list.length !== 0 && (
                    <SourceDataExpandingContext.Provider value={context}>
                        <Virtuoso
                            context={{ Layout, settings, events, list, expanded }}
                            increaseViewportBy={400}
                            ref={virtuoso}
                            useWindowScroll
                            data={filteredList}
                            itemContent={virtuosoItemContent}
                        />
                    </SourceDataExpandingContext.Provider>
                )
            }
        </>
    );
}

export default SourceDataFormLayoutExpanding;